import { useEffect } from 'react';
import { OrganizationType } from 'modules/Common/utils/Utility';
import { useLocaleStore, getPreferredLocale } from 'stores/useLocaleStore';
import { DEFAULT_LOCALE } from 'i18n/locales';
import { applyLocale } from 'i18n';

/**
 * Which organization types may choose a UI language.
 *
 * TEMPORARY LIMITATION: operator and consultant surfaces are English-only —
 * they have not been through translation review, so a picker there would offer
 * languages the pages do not actually speak. Declarants keep the picker. When
 * those surfaces are translated, delete ENGLISH_ONLY_ORG_TYPES (and the two
 * call sites that hide the control) and the pin machinery in useLocaleStore
 * unpins itself on the next app load.
 */
const ENGLISH_ONLY_ORG_TYPES = [OrganizationType.Operator, OrganizationType.Consultant];

/**
 * @param {string|undefined|null} orgType `publicMetadata.type` of the active org.
 * @returns {boolean} false only for a type we KNOW is English-only — an
 * unresolved org type must not hide a control the user is entitled to.
 */
export const isLanguageSelectionAllowed = (orgType) => !ENGLISH_ONLY_ORG_TYPES.includes(orgType);

/**
 * Hold the UI in English for as long as an English-only org is active, and give
 * the user's own language back the moment one isn't.
 *
 * Mounted once, in OrgBoundary, on the authoritative active org — hiding the
 * pickers is not enough on its own: the locale is device-scoped and persisted,
 * so an operator whose browser is German (or who picked German before this
 * limitation existed) would otherwise sit in a half-translated German UI with
 * no way out.
 *
 * @param {string|undefined|null} orgType `publicMetadata.type` of the active org.
 */
export function useEnforceOrgLanguagePolicy(orgType) {
  const pinLocale = useLocaleStore((s) => s.pinLocale);

  useEffect(() => {
    // Org not resolved yet (boot, mid-switch): leave the locale alone rather
    // than flipping the UI to English and back.
    if (!orgType) return undefined;
    const pin = isLanguageSelectionAllowed(orgType) ? null : DEFAULT_LOCALE;
    const next = pin ?? getPreferredLocale();
    // Strict no-op guard: applyLocale() clears the React Query cache on every
    // call, so it must fire only on a real change — not on each org mount.
    if (next === useLocaleStore.getState().locale) return undefined;

    let cancelled = false;
    // Resources first, store second — same ordering the pickers document: the
    // store update re-renders every subscriber, and doing it first flashes the
    // whole UI through untranslated English.
    applyLocale(next)
      .catch((error) => {
        console.error('Failed to apply the organization language policy:', error);
      })
      .finally(() => {
        if (!cancelled) pinLocale(pin);
      });
    return () => {
      cancelled = true;
    };
  }, [orgType, pinLocale]);
}
